import React, { useState, useEffect } from "react";
import { Save, X, Plus, UserPlus, Users, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminEventEditor = ({ event, onClose, onSaved }) => {
  const existingTypes = (event.ticket_types && event.ticket_types.length > 0)
    ? event.ticket_types.map(tt => ({
        name: tt.name || "", price: tt.price?.toString() || "0",
        quantity: tt.quantity?.toString() || "0", description: tt.description || ""
      }))
    : [{ name: "Standard", price: event.price?.toString() || "0", quantity: event.available_tickets?.toString() || "100", description: "" }];

  const hasRealMultiTarif = event.ticket_types && (event.ticket_types.length > 1 || (event.ticket_types.length === 1 && event.ticket_types[0].name !== "Standard"));

  const [form, setForm] = useState({
    title: event.title || "",
    artist: event.artist || "",
    date: event.date || "",
    time: event.time || "",
    venue: event.venue || "",
    location: event.location || "",
    price: event.price?.toString() || "0",
    category: event.category || "Hip-Hop/RNB",
    description: event.description || "",
    image: event.image || "",
    available_tickets: event.available_tickets?.toString() || "100",
    service_fee: (event.service_fee !== undefined ? event.service_fee : 0.99).toString(),
    conditions: event.conditions || "",
  });
  const [useMultiTarif, setUseMultiTarif] = useState(hasRealMultiTarif);
  const [ticketTypes, setTicketTypes] = useState(existingTypes);
  const [saving, setSaving] = useState(false);

  const addTicketType = () => {
    setTicketTypes([...ticketTypes, { name: "", price: "", quantity: "", description: "" }]);
  };

  const removeTicketType = (index) => {
    if (ticketTypes.length > 1) {
      setTicketTypes(ticketTypes.filter((_, i) => i !== index));
    }
  };

  const updateTicketType = (index, field, value) => {
    const updated = [...ticketTypes];
    updated[index] = { ...updated[index], [field]: value };
    setTicketTypes(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("tika_admin_token");

      // Build full event data for PUT (full replace)
      const eventData = {
        title: form.title,
        artist: form.artist,
        date: form.date,
        time: form.time,
        venue: form.venue,
        location: form.location,
        price: parseFloat(form.price) || 0,
        category: form.category,
        description: form.description,
        image: form.image,
        available_tickets: parseInt(form.available_tickets) || 100,
        service_fee: parseFloat(form.service_fee) || 0,
        conditions: form.conditions,
        ticket_types: useMultiTarif
          ? ticketTypes.map(tt => ({
              name: tt.name,
              price: parseFloat(tt.price) || 0,
              quantity: parseInt(tt.quantity) || 0,
              description: tt.description || ""
            }))
          : null,
      };

      await axios.put(`${API}/admin/events/${event.id}`, eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Événement mis à jour");
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-event-editor">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">Modifier : {event.title}</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Titre</Label>
          <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Artiste</Label>
          <Input value={form.artist} onChange={(e) => setForm({...form, artist: e.target.value})} className="mt-1" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Date</Label>
          <Input value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="mt-1" placeholder="Samedi 15 Mars 2025" />
        </div>
        <div>
          <Label className="text-xs">Horaire</Label>
          <Input value={form.time} onChange={(e) => setForm({...form, time: e.target.value})} className="mt-1" placeholder="22h00 - 05h00" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Lieu</Label>
          <Input value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Adresse / Ville</Label>
          <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="mt-1" />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Prix de base (€)</Label>
          <Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="mt-1" min="0" step="0.01" />
        </div>
        <div>
          <Label className="text-xs">Billets dispo</Label>
          <Input type="number" value={form.available_tickets} onChange={(e) => setForm({...form, available_tickets: e.target.value})} className="mt-1" min="0" disabled={useMultiTarif} />
        </div>
        <div>
          <Label className="text-xs">Frais service (€)</Label>
          <Input type="number" value={form.service_fee} onChange={(e) => setForm({...form, service_fee: e.target.value})} className="mt-1" min="0" step="0.01" />
        </div>
      </div>

      {/* Multi-tarif section */}
      <div className="glass-card rounded-lg p-4 border border-primary/20">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-medium text-sm">Multi-tarifs</h4>
            <p className="text-xs text-muted-foreground">Plusieurs types de billets</p>
          </div>
          <Button
            type="button"
            variant={useMultiTarif ? "default" : "outline"}
            size="sm"
            onClick={() => setUseMultiTarif(!useMultiTarif)}
          >
            {useMultiTarif ? "Activé" : "Activer"}
          </Button>
        </div>

        {useMultiTarif && (
          <div className="space-y-3 pt-3 border-t border-border">
            {ticketTypes.map((tt, index) => (
              <div key={index} className="glass-card rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium">Type #{index + 1}</span>
                  {ticketTypes.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeTicketType(index)}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Nom" value={tt.name} onChange={(e) => updateTicketType(index, 'name', e.target.value)} className="text-sm" />
                  <Input type="number" placeholder="Prix €" value={tt.price} onChange={(e) => updateTicketType(index, 'price', e.target.value)} className="text-sm" min="0" step="0.01" />
                  <Input type="number" placeholder="Qté" value={tt.quantity} onChange={(e) => updateTicketType(index, 'quantity', e.target.value)} className="text-sm" min="0" />
                </div>
                <Input placeholder="Description (optionnel)" value={tt.description} onChange={(e) => updateTicketType(index, 'description', e.target.value)} className="text-xs" />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addTicketType} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un type
            </Button>
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs">Catégorie</Label>
        <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
          <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Hip-Hop/RNB">Hip-Hop/RNB</SelectItem>
            <SelectItem value="Afrobeat">Afrobeat</SelectItem>
            <SelectItem value="Dancehall/Caraïbes">Dancehall/Caraïbes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Image (URL)</Label>
        <div className="flex gap-2 mt-1 items-center">
          {form.image && <img src={form.image} alt="" className="w-16 h-16 rounded object-cover" />}
          <Input value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} placeholder="https://..." className="flex-1" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="mt-1" />
      </div>

      <div>
        <Label className="text-xs">Conditions du billet</Label>
        <Textarea value={form.conditions} onChange={(e) => setForm({...form, conditions: e.target.value})} rows={3} className="mt-1" placeholder="Non remboursable, droit d'entrée réservé..." />
      </div>

      {/* Admin Co-organizer management */}
      <AdminCoOrgSection eventId={event.id} />

      <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-event-btn">
        <Save className="w-4 h-4 mr-2" />
        {saving ? "Enregistrement..." : "Enregistrer les modifications"}
      </Button>
    </div>
  );
};


const AdminCoOrgSection = ({ eventId }) => {
  const [coOrgs, setCoOrgs] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchCoOrgs = async () => {
    try {
      const token = localStorage.getItem("tika_admin_token");
      // Use admin endpoint to get event details including co_organizers
      const res = await axios.get(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const event = (res.data || []).find(e => e.id === eventId);
      if (event && event.co_organizers && event.co_organizers.length > 0) {
        // Resolve organizer names from IDs
        const orgDetails = [];
        for (const coId of event.co_organizers) {
          try {
            const orgRes = await axios.get(`${API}/organizer/${coId}/public`);
            orgDetails.push({ id: coId, name: orgRes.data.name || orgRes.data.company_name, email: "" });
          } catch {
            orgDetails.push({ id: coId, name: coId, email: "" });
          }
        }
        setCoOrgs(orgDetails);
      } else {
        setCoOrgs([]);
      }
    } catch (err) {
      console.error("Erreur co-org:", err);
    }
  };

  useEffect(() => {
    if (eventId) fetchCoOrgs();
  }, [eventId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const res = await axios.post(
        `${API}/admin/events/${eventId}/co-organizers`,
        { organizer_email: email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setEmail("");
      fetchCoOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card rounded-lg p-3 border border-primary/20">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-primary" />
        <h4 className="font-medium text-xs">Co-organisateurs</h4>
      </div>

      {coOrgs.length > 0 && (
        <div className="space-y-1 mb-2">
          {coOrgs.map((co) => (
            <div key={co.id} className="flex items-center justify-between bg-muted/20 rounded px-2 py-1.5 text-xs">
              <span className="truncate">{co.name}</span>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleAdd} className="flex gap-2">
        <div className="relative flex-1">
          <Mail className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email organisateur" className="pl-7 text-xs h-8" />
        </div>
        <Button type="submit" size="sm" disabled={loading || !email.trim()} className="h-8 text-xs">
          <UserPlus className="w-3 h-3 mr-1" />
          Ajouter
        </Button>
      </form>
    </div>
  );
};
